/**
 * Attention Component implemented with Material UI
 * 
 * This component provides an attention indicator that glows when there are errors
 * in the application.
 */
import React from 'react';
import { styled, keyframes } from '@mui/material/styles';
import { Box } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import ReducersIndex from 'reduxModules/ducks';
import { getErrorState } from 'reduxModules/ducks/ui';
import { MuiIcon } from './index';
import { AttentionProps } from 'types/components/atoms';
import { RootState } from 'types/redux/state';
import { AnyAction } from '@reduxjs/toolkit';

/**
 * Attention component
 * 
 * @param props - Component props
 * @returns Attention indicator component
 */
const Attention: React.FC<AttentionProps> = (props) => {
  const { onClick, animated = true, ...rest } = props;
  
  // Get error state from Redux
  const error = useSelector((state: RootState) => getErrorState(state));
  const dispatch = useDispatch();
  
  // Handle click to open warning modal
  const handleClick = () => {
    if (onClick) {
      onClick();
    } else {
      // Default behavior: open warning modal
      dispatch(ReducersIndex.uiReducers.openModal('warning') as AnyAction);
    }
  };

  // Only render if there's an error
  if (!error) return null;

  return (
    <Wrapper onClick={handleClick} $animated={animated} {...rest}>
      <MuiIcon 
        icon="attention" 
        size="34px"
        state=""
        scale={1}
        usage="error"
      />
    </Wrapper>
  );
};

// Define keyframes animation
const glow = keyframes`
  0% { opacity: 1 }
  20% { opacity: 1 }
  100% { opacity: 0.1 }
`;

// Interface for styled component props
interface WrapperProps {
  $animated?: boolean;
}

// Styled components using MUI styled API
const Wrapper = styled(Box)<WrapperProps>(({ $animated }) => ({
  display: 'flex',
  alignItems: 'center',
  minWidth: '34px',
  minHeight: '34px',
  animation: $animated ? `${glow} 1s infinite alternate ease-in` : 'none',
  cursor: 'pointer',
}));

export type { AttentionProps };
export default Attention;
